// src/contexts/administrativo/FilialContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchData, postData, putData, deleteData } from '../../services/Api';
import {
  deleteFilialLogoDelivery as deleteFilialLogoDeliveryService,
  fetchFilialLogo as fetchFilialLogoService,
  fetchFilialLogoDelivery as fetchFilialLogoDeliveryService,
  updateFilialLogo as updateFilialLogoService,
  updateFilialLogoDelivery as updateFilialLogoDeliveryService,
} from '../../services/administrativo/FilialLogoService';
import { useAlert } from '../geral/AlertContext';

const FilialContext = createContext();

export const useFilial = () => useContext(FilialContext);

export const FilialProvider = ({ children }) => {
  const [filiais, setFiliais] = useState([]);
  const [filialLogin, setFilialLogin] = useState(null);
  const [logoFilialLogin, setLogoFilialLogin] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showAlert } = useAlert();

  const fetchFilialLogo = useCallback(async (filialId) => {
    if (!filialId) return null;

    try {
      return await fetchFilialLogoService(filialId);
    } catch (error) {
      // Logo pode não existir ainda para a filial (ex.: 404)
      if (error?.status !== 404) {
        console.error('Erro ao buscar logo da filial:', error);
      }
      return null;
    }
  }, []);

  const updateFilialLogo = useCallback(async (filialId, logoBase64) => {
    if (!filialId) return null;

    try {
      const result = await updateFilialLogoService(filialId, logoBase64);
      const logoAtualizada = typeof result === 'string'
        ? result
        : (logoBase64 || null);

      if (filialLogin?.id === filialId) {
        setLogoFilialLogin(logoAtualizada);
      }

      return logoAtualizada;
    } catch (error) {
      console.error('Erro ao salvar logo da filial:', error);
      showAlert('Erro ao salvar logo da filial!', 'danger');
      throw error;
    }
  }, [filialLogin, showAlert]);

  const fetchFilialLogoDelivery = useCallback(async (filialId) => {
    try {
      return await fetchFilialLogoDeliveryService(filialId);
    } catch (error) {
      if (error?.status !== 404) {
        console.error('Erro ao buscar logo do delivery:', error);
      }
      return null;
    }
  }, []);

  const updateFilialLogoDelivery = useCallback(async (filialId, imagemBase64) => {
    try {
      const result = await updateFilialLogoDeliveryService(filialId, imagemBase64);
      showAlert('Logo do delivery atualizada com sucesso!');
      return result;
    } catch (error) {
      console.error('Erro ao salvar logo do delivery:', error);
      showAlert(error?.message || 'Erro ao salvar logo do delivery!', 'danger');
      throw error;
    }
  }, [showAlert]);

  const deleteFilialLogoDelivery = useCallback(async (filialId) => {
    try {
      await deleteFilialLogoDeliveryService(filialId);
      showAlert('Logo do delivery removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover logo do delivery:', error);
      showAlert(error?.message || 'Erro ao remover logo do delivery!', 'danger');
      throw error;
    }
  }, [showAlert]);

  const fetchFiliais = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchData('/Filial');
      setFiliais(result);
      setFilialLogin(result[0] || null);

      return result; // Retorna as filiais para uso imediato
      
    } catch (error) {
      console.error('Erro ao buscar filiais:', error);
      showAlert('Erro ao buscar filiais!', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchFiliais();
  }, [fetchFiliais]);

  useEffect(() => {
    const loadLogoFilialLogin = async () => {
      if (!filialLogin?.id) {
        setLogoFilialLogin(null);
        return;
      }

      const logo = await fetchFilialLogo(filialLogin.id);
      setLogoFilialLogin(logo);
    };

    loadLogoFilialLogin();
  }, [filialLogin, fetchFilialLogo]);

  const addFilial = async (filial) => {
    try {
      const result = await postData('/Filial', filial);
      setFiliais((prev) => [...prev, result]);
      if (!filialLogin) setFilialLogin(result);
      showAlert('Filial adicionada com sucesso!');
      return result;
    } catch (error) {
      console.error('Erro ao adicionar filial:', error);
      showAlert('Erro ao adicionar filial!', 'danger');
      throw error;
    }
  };

  const updateFilial = async (filial) => {
    try {
      const result = await putData('/Filial', filial);
      setFiliais((prev) => prev.map((f) => (f.id === result.id ? result : f)));
      setFilialLogin((prev) => (prev?.id === result.id ? result : prev));
      showAlert('Filial alterada com sucesso!');
      return result;
    } catch (error) {
      console.error('Erro ao atualizar filial:', error);
      showAlert('Erro ao alterar filial!', 'danger');
      throw error;
    }
  };

  const deleteFilial = async (id) => {
    try {
      await deleteData('/Filial', { filialId: id });
      setFiliais((prev) => prev.filter((f) => f.id !== id));
      setFilialLogin((prev) => (prev?.id === id ? null : prev));
      showAlert('Filial excluida com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir filial:', error);
      showAlert('Erro ao excluir filial!', 'danger');
    }
  };

  const value = useMemo(
    () => ({
      filiais,
      filialLogin,
      logoFilialLogin,
      setFilialLogin,
      setLogoFilialLogin,
      loading,
      fetchFiliais,
      fetchFilialLogo,
      updateFilialLogo,
      fetchFilialLogoDelivery,
      updateFilialLogoDelivery,
      deleteFilialLogoDelivery,
      addFilial,
      updateFilial,
      deleteFilial
    }),
    [
      filiais,
      filialLogin,
      logoFilialLogin,
      loading,
      fetchFiliais,
      fetchFilialLogo,
      updateFilialLogo,
      fetchFilialLogoDelivery,
      updateFilialLogoDelivery,
      deleteFilialLogoDelivery,
    ]
  );

  return <FilialContext.Provider value={value}>{children}</FilialContext.Provider>;
};
